import { ChevronLeft, ChevronRight } from 'assets/icons';
import PanelCard, { cardIconButtonClass } from 'components/card/PanelCard';
import useOpenInvoiceDetail from 'components/invoiceDetail/useOpenInvoiceDetail';
import { fillText } from 'components/invoiceImport/invoiceImportText';
import { formatEuros } from 'components/invoiceList/invoiceAmount';
import InvoiceStatusBadge from 'components/invoiceList/InvoiceStatusBadge';
import { periodLabel } from 'components/SlideOver/dataSlideOver/data/slideOverCompany/CompanyBillingHistoryCard';
import UseContext from 'hooks/useContext';
import { useEffect, useRef, useState } from 'react';
import ApiDatabase from 'server';

const HEADER_CELL_CLASS = 'px-2 py-2 font-normal text-gray-500 text-left uppercase tracking-wide';
// What was never billed counts neither in the total nor in what is left to pay.
const NOT_BILLED = ['cancelled', 'draft', 'error', 'not_imported'];

// One company's invoices of a year. Each company has its own `abortKey`: under
// the shared one, the reads of a group would cancel each other.
const readInvoices = (company, year) =>
  new Promise((resolve) => {
    ApiDatabase.getCompanyInvoices(
      { idCompany: company.id, year },
      (response) => resolve({ company, data: response?.data ?? {} }),
      (error) => resolve({ company, error }),
      { abortKey: `group-invoices:${company.id}` },
    );
  });

/**
 * « Historique de facturation » of a group: the invoices of all its companies,
 * one year at a time, on the model of a company's (CompanyBillingHistoryCard),
 * with the company each one is for, then the total of the year. A row opens
 * the invoice detail. A company whose invoices could not be read is named
 * above the others, which stay on screen. `companyId`: only that company's
 * invoices are shown, from the same reads (null: all of them).
 */
const GroupBillingHistoryCard = ({ companies, companyId = null }) => {
  const { language } = UseContext();
  const openInvoiceDetail = useOpenInvoiceDetail();
  const currentYear = new Date().getFullYear();
  const [requestedYear, setRequestedYear] = useState(currentYear);
  const [history, setHistory] = useState(null);
  // Kept across reads: the previous year stays reachable while one loads.
  const [knownYears, setKnownYears] = useState([]);
  const [status, setStatus] = useState('loading');
  const [retryCount, setRetryCount] = useState(0);
  const text = (key, fallback) => language.getValue(`companyBillingHistory.${key}`, fallback);
  // The page hands a new list at each of its reloads: the invoices are read
  // again only when the group gains or loses a company.
  const companiesRef = useRef(companies);
  companiesRef.current = companies;
  const ids = companies.map((company) => company.id).join(',');

  useEffect(() => {
    const list = companiesRef.current;
    if (!list.length) return undefined;
    // An answer for a year the user has since left must not replace the one
    // asked for after it.
    let isCurrent = true;
    setStatus('loading');
    Promise.all(list.map((company) => readInvoices(company, requestedYear))).then((reads) => {
      if (!isCurrent) return;
      const answered = reads.filter((read) => !read.error);
      if (!answered.length) {
        setStatus('error');
        return;
      }
      setHistory({
        year: requestedYear,
        items: answered.flatMap(({ company, data }) => (data.items ?? []).map((item) => ({ ...item, companyId: company.id }))),
        failedIds: reads.filter((read) => read.error).map((read) => read.company.id),
      });
      setKnownYears([...new Set(answered.flatMap(({ data }) => data.years ?? []))]);
      setStatus('ready');
    });
    return () => {
      isCurrent = false;
    };
  }, [ids, requestedYear, retryCount]);

  const nameOf = (id) => companies.find((company) => company.id === id)?.name ?? '';
  const isShown = (id) => !companyId || id === companyId;
  const shownItems = status === 'ready' && history ? history.items.filter((item) => isShown(item.companyId)) : [];
  const shownYear = status === 'ready' && history ? history.year : requestedYear;
  const oldestYear = knownYears.length ? Math.min(...knownYears) : null;
  const isPreviousDisabled = oldestYear === null || shownYear <= oldestYear;
  const isNextDisabled = shownYear >= currentYear;
  const retry = () => setRetryCount((count) => count + 1);

  // The arrows sit on the header that folds the card: neither a click nor
  // Enter/Space on them may reach it.
  const stopFolding = (event) => event.stopPropagation();

  const yearSelector = (
    <div className={'flex items-center gap-2'}>
      <button
        type={'button'}
        aria-label={text('previousYear', 'Année précédente')}
        className={`${cardIconButtonClass} disabled:opacity-40 disabled:cursor-not-allowed`}
        disabled={isPreviousDisabled}
        onClick={(event) => {
          event.stopPropagation();
          setRequestedYear(shownYear - 1);
        }}
        onKeyDown={stopFolding}
      >
        <ChevronLeft wh={18} color={'#374151'} />
      </button>
      <span className={'border border-gray-300 rounded-md h-8 px-4 flex items-center font-medium text-gray-900'}>
        {shownYear}
      </span>
      <button
        type={'button'}
        aria-label={text('nextYear', 'Année suivante')}
        className={`${cardIconButtonClass} disabled:opacity-40 disabled:cursor-not-allowed`}
        disabled={isNextDisabled}
        onClick={(event) => {
          event.stopPropagation();
          setRequestedYear(shownYear + 1);
        }}
        onKeyDown={stopFolding}
      >
        <ChevronRight wh={18} color={'#374151'} />
      </button>
    </div>
  );

  const retryButton = (
    <button
      type={'button'}
      className={'border border-gray-300 rounded-md px-3 py-1 hover:bg-gray-50 flex-shrink-0'}
      onClick={retry}
    >
      {text('retry', 'Réessayer')}
    </button>
  );

  const content = () => {
    if (status === 'loading') {
      return <div className={'px-4 py-3 text-gray-500'}>{text('loading', 'Chargement…')}</div>;
    }
    if (status === 'error') {
      return (
        <div className={'px-4 py-3 flex items-center justify-between gap-2'}>
          <span className={'text-red-500'}>
            {text('loadError', 'L’historique de facturation n’a pas pu être chargé.')}
          </span>
          {retryButton}
        </div>
      );
    }
    const failed = history.failedIds.filter(isShown).map(nameOf).filter(Boolean);
    // The latest month first, then the companies in alphabetical order.
    const rows = [...shownItems].sort(
      (a, b) =>
        String(b.period).localeCompare(String(a.period)) ||
        nameOf(a.companyId).localeCompare(nameOf(b.companyId), 'fr') ||
        Number(b.number) - Number(a.number),
    );
    return (
      <>
        {failed.length > 0 && (
          <div className={'px-4 py-3 flex items-center justify-between gap-2'}>
            <span className={'text-red-500'}>
              {fillText(text('groupPartialError', 'Les factures de {names} n’ont pas pu être chargées.'), {
                names: failed.join(', '),
              })}
            </span>
            {retryButton}
          </div>
        )}
        {!rows.length ? (
          <div className={'px-4 py-3 text-gray-500'}>
            {fillText(
              companyId
                ? text('empty', 'Aucune facture en {year}')
                : text('groupEmpty', 'Aucune facture en {year} pour les entreprises du groupe'),
              { year: history.year },
            )}
          </div>
        ) : (
          <div className={'overflow-x-auto'}>
            <table className={'w-full text-sm'}>
              <thead>
                <tr className={'border-b border-gray-100'}>
                  <th className={`${HEADER_CELL_CLASS} pl-4`}>{text('columnPeriod', 'Période')}</th>
                  <th className={HEADER_CELL_CLASS}>{text('columnCompany', 'Entreprise')}</th>
                  <th className={HEADER_CELL_CLASS}>{text('columnNumber', 'N°')}</th>
                  <th className={HEADER_CELL_CLASS}>{text('columnAmount', 'Montant TTC')}</th>
                  <th className={`${HEADER_CELL_CLASS} pr-4`}>{text('columnPayment', 'Paiement')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => (
                  <tr
                    key={`${item.companyId}-${item.number}`}
                    className={'border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer'}
                    onClick={() => openInvoiceDetail(item.number)}
                  >
                    <td className={'py-3 pl-4 pr-2 whitespace-nowrap'}>{periodLabel(item.period)}</td>
                    <td className={'px-2 py-3'}>
                      <span className={'block max-w-[14rem] truncate uppercase'}>{nameOf(item.companyId)}</span>
                    </td>
                    <td className={'px-2 py-3 whitespace-nowrap'}>
                      {/* The keyboard way in: its click bubbles to the row, which
                          opens the detail, so it carries no handler of its own. */}
                      <button
                        type={'button'}
                        className={'rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400'}
                        aria-label={fillText(text('openInvoice', 'Voir la facture {number}'), { number: item.number })}
                      >
                        {item.number}
                      </button>
                    </td>
                    <td className={'px-2 py-3 whitespace-nowrap'}>{formatEuros(item.amountInclTax)}</td>
                    <td className={'py-3 pl-2 pr-4'}>
                      <InvoiceStatusBadge invoice={item} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  };

  // The total of the year, and what is still to pay on it.
  const billed = shownItems.filter((item) => !NOT_BILLED.includes(item.status));
  const total = billed.reduce((sum, item) => sum + Number(item.amountInclTax || 0), 0);
  const remaining = billed.reduce((sum, item) => sum + Math.max(0, Number(item.remainingAmount || 0)), 0);
  const totalText =
    billed.length > 1
      ? text('groupTotalMany', 'Total {year} : {amount} TTC sur {count} factures')
      : text('groupTotalOne', 'Total {year} : {amount} TTC sur {count} facture');
  const footer = billed.length > 0 && (
    <p className={'px-3 pb-2 pt-1 text-right text-sm text-gray-600'} data-testid={'group-billing-total'}>
      {fillText(totalText, { year: history.year, amount: formatEuros(total), count: billed.length })}
      {remaining > 0 &&
        `, ${fillText(text('groupRemaining', 'dont {amount} restant à payer'), { amount: formatEuros(remaining) })}`}
    </p>
  );

  return (
    <PanelCard title={text('cardTitle', 'Historique de facturation')} badge={yearSelector} collapsible footer={footer}>
      {content()}
    </PanelCard>
  );
};

export default GroupBillingHistoryCard;
